<template>
  <div>
    <h2>Specific Heat of a Metal: Data + Calculations</h2>
    <v-form>
      <!-- Data Table -->
      <h3 class="pt-6 pb-4">Data Table</h3>
      <v-textarea
        v-model="inputs.data_table_caption"
        label="Table Caption"
        outlined
        rows="1"
        class="mb-n3"
        placeholder="Write a caption for the data table here."
      />
      <v-simple-table>
        <thead>
          <tr>
            <td>&nbsp;</td>
            <td style="font-weight: bold; text-align: center">Units</td>
            <td style="font-weight: bold; text-align: center">Trial 1</td>
            <td style="font-weight: bold; text-align: center">Trial 2</td>
          </tr>
        </thead>
        <tr>
          <th style="text-align: left">Identity</th>
          <td>&nbsp;</td>
          <td v-for="i in 2" :key="`known-${i}`">
            <v-select
              v-model="inputs[`trial${i}_identity`]"
              :items="['Zn', 'Cu']"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex class="no-text-transform" :content="item" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item" />
              </template>
            </v-select>
          </td>
        </tr>
        <tr v-for="{label, key, units: rowUnits} in dataTableRows" :key="key">
          <th style="text-align: left">
            <stemble-latex :content="label" />
          </th>
          <td>
            <v-select
              v-if="!!rowUnits"
              v-model="inputs[`${key}_units`]"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
            <span v-else>&nbsp;</span>
          </td>
          <td v-for="i in 2" :key="`known-${key}-${i}`">
            <calculation-input v-model="inputs[`trial${i}_${key}`]" class="centered-input" />
          </td>
        </tr>
      </v-simple-table>

      <!-- Data Upload -->
      <h3 class="pt-8 pb-3">Graph Upload</h3>

      <p class="mb-2">
        You have completed 2 trials in this part of the experiment and uploaded the graphs for both
        in an earlier task. Here, upload the graph of one of the two trials using the input field
        below. Your graph must be clearly titled and in pdf format.
      </p>

      <p class="mb-5">
        <file-upload-ui-component
          style="width: 100%"
          label="Click here to select your file"
          :max-number-of-files="1"
          :max-file-size="10"
          accepted-file-extensions=".pdf"
          @upload:attachments="attachments = $event"
        />
      </p>

      <!-- Calculations -->
      <h3 class="pt-4 pb-3">Calculations</h3>

      <v-textarea
        v-model="inputs.calculations_table_caption"
        label="Table Caption"
        outlined
        rows="1"
        class="mb-n3"
        placeholder="Write a caption for the calculations table here."
      />
      <v-simple-table>
        <thead>
          <tr>
            <td>&nbsp;</td>
            <td style="font-weight: bold; text-align: center">Units</td>
            <td style="font-weight: bold; text-align: center">Trial 1</td>
            <td style="font-weight: bold; text-align: center">Trial 2</td>
          </tr>
        </thead>
        <tr
          v-for="{label, key, units: rowUnits, type: inputType} in calculationTableRows"
          :key="key"
        >
          <th style="text-align: left">
            <stemble-latex :content="label" />
          </th>
          <td>
            <v-select
              v-if="!!rowUnits"
              v-model="inputs[`${key}_units`]"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
            <span v-else>&nbsp;</span>
          </td>
          <td v-for="i in 2" :key="`known-${key}-${i}`">
            <v-textarea
              v-if="inputType === 'string'"
              v-model="inputs[`trial${i}_${key}`]"
              outlined
              solo
              rows="3"
            />
            <calculation-input v-else v-model="inputs[`trial${i}_${key}`]" class="centered-input" />
          </td>
        </tr>
      </v-simple-table>

      <p class="mt-5">
        In the space provided below, please type out your sample calculations for this part of the
        experiment. Be sure to show all your work.
      </p>

      <v-textarea v-model="inputs.sampleCalc" outlined rows="6" auto-grow />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import StembleLatex from '@/tasks/components/StembleLatex.vue';
import FileUploadUiComponent from '@/tasks/components/task-ui-components/FileUploadUiComponent.vue';

export default {
  name: 'OttawaLab2ReportTask2Data',
  components: {FileUploadUiComponent, StembleLatex, CalculationInput},
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        data_table_caption: null,
        calculations_table_caption: null,

        mass_metal_units: null,
        initial_temp_metal_units: null,
        mass_calorimeter_units: null,
        volume_water_units: null,
        mass_calorimeter_and_water_units: null,
        initial_temp_water_units: null,
        final_temp_mixture_units: null,
        mass_water_units: null,
        delta_temp_water_units: null,
        specific_heat_water_units: null,
        delta_temp_metal_units: null,
        specific_heat_metal_units: null,
        literature_specific_heat_metal_units: null,
        percent_error_specific_heat_metal_units: null,

        trial1_identity: null,
        trial2_identity: null,
        trial1_mass_metal: null,
        trial2_mass_metal: null,
        trial1_initial_temp_metal: null,
        trial2_initial_temp_metal: null,
        trial1_mass_calorimeter: null,
        trial2_mass_calorimeter: null,
        trial1_volume_water: null,
        trial2_volume_water: null,
        trial1_mass_calorimeter_and_water: null,
        trial2_mass_calorimeter_and_water: null,
        trial1_initial_temp_water: null,
        trial2_initial_temp_water: null,
        trial1_final_temp_mixture: null,
        trial2_final_temp_mixture: null,
        trial1_mass_water: null,
        trial2_mass_water: null,
        trial1_delta_temp_water: null,
        trial2_delta_temp_water: null,
        trial1_specific_heat_water: null,
        trial2_specific_heat_water: null,
        trial1_reference_specific_heat_water: null,
        trial2_reference_specific_heat_water: null,
        trial1_delta_temp_metal: null,
        trial2_delta_temp_metal: null,
        trial1_specific_heat_metal: null,
        trial2_specific_heat_metal: null,
        trial1_literature_specific_heat_metal: null,
        trial2_literature_specific_heat_metal: null,
        trial1_reference_specific_heat_metal: null,
        trial2_reference_specific_heat_metal: null,
        trial1_percent_error_specific_heat_metal: null,
        trial2_percent_error_specific_heat_metal: null,

        sampleCalc: null,
      },
      attachments: [],
      units: [
        {key: 'g', label: '$\\text{g}$'},
        {key: 'C', label: '$^{\\omicron}\\text{C}$'},
        {key: 'mL', label: '$\\text{mL}$'},
        {
          key: 'JGC',
          label: '$\\text{J} \\cdot \\text{g}^{-1} \\cdot {^{\\omicron}\\text{C}}^{-1}$',
        },
        {
          key: 'percent',
          label: '$\\%$',
        },
      ],
      dataTableRows: [
        {label: 'Mass of Metal', key: 'mass_metal', units: true},
        {label: 'Initial Temperature of Metal', key: 'initial_temp_metal', units: true},
        {label: 'Mass of Calorimeter', key: 'mass_calorimeter', units: true},
        {label: 'Volume of Water', key: 'volume_water', units: true},
        {label: 'Mass (Water + Calorimeter)', key: 'mass_calorimeter_and_water', units: true},
        {label: 'Initial Temperature of Water', key: 'initial_temp_water', units: true},
        {label: 'Temperature of Mixture', key: 'final_temp_mixture', units: true},
      ],
      calculationTableRows: [
        {label: 'Mass of Water', key: 'mass_water', units: true},
        {label: '$\\Delta T_{water}$', key: 'delta_temp_water', units: true},
        {label: '$c_{water}$', key: 'specific_heat_water', units: true},
        {
          label: 'Reference for $c_{water}$',
          key: 'reference_specific_heat_water',
          units: false,
          type: 'string',
        },
        {label: '$\\Delta T_{metal}$', key: 'delta_temp_metal', units: true},
        {label: '$c_{metal}$ (Experimental)', key: 'specific_heat_metal', units: true},
        {label: '$c_{metal}$ (Literature)', key: 'literature_specific_heat_metal', units: true},
        {
          label: 'Reference for $c_{metal}$',
          key: 'reference_specific_heat_metal',
          units: false,
          type: 'string',
        },
        {label: 'Percent Error', key: 'percent_error_specific_heat_metal', units: true},
      ],
    };
  },
};
</script>
